<template>
  <label
    :for="inputId"
    :class="[
      $style.container,
      disabled && $style.containerDisabled,
      theme && $style[`container${theme}`],
      theme && noMargin && $style[`container${theme}NoMargin`],
      size && $style[size]
    ]"
    :data-testid="dataTestid">
    <div
      :class="[reorder && $style.inputReordered, 'relative']"
      @mouseover="hover = true"
      @mouseleave="hover = false"
    >
      <input
        :type="mode"
        :id="inputId"
        :name="name"
        :class="$style.realCheck"
        :checked="checked"
        :disabled="disabled"
        :data-testid="`${dataTestid}-input`"
        ref="choiceInput"
        @input="onInput"
      >
      <b-tooltip
        v-if="tooltip"
        :description="tooltip"
        :visible="hover"
        tooltipWidth="300px"
        :position="tooltipPosition"
      />
      <div
        v-if="theme === 'default' || theme === 'radio'"
        :class="[
          $style.replacement,
          checked && $style.replacementChecked,
          partiallyChecked && !disabled && $style.replacementPartiallyChecked,
          invalid && $style.replacementInvalid,
          (mode === 'radio' || (mode === 'checkbox' && theme === 'radio')) && $style.replacementRadio
        ]"
      >
        <b-svg
          v-if="mode === 'checkbox' && theme !== 'radio'"
          :name="partiallyChecked ? 'minus' : 'tick'"
          :class="$style.tick"
        />
      </div>
      <div
        v-if="theme.includes('toggler')"
        :class="[
          $style.toggler,
          theme === 'toggler--revert' && $style.togglerRevert,
          theme === 'toggler--revert-order' && $style.togglerRevertOrder,
          theme === 'toggler--revert-order-grayed' && $style.togglerRevertOrderGrayed,
          checked && $style.togglerChecked,
        ]"
      />
      <slot name="text" :checked="checked" :class-name="$style.valueDescription" />
    </div>
    <div
      v-if="$slots.default"
      :class="[
        $style.label,
        reorder && $style.contentReordered
      ]"
    >
      <slot />
    </div>
  </label>
</template>

<script>
import { computed } from 'vue'
import { useLogger } from '@booksy-com/vue-tools'
import { TOOLTIP_POSITIONS } from '@/lib/consts.ts'

export default {

  emits: [
    'input',
  ],
  setup (props, { attrs }) {
    const logger = useLogger('b-input-choice')
    const dataTestid = computed(() => attrs['data-testid'] || props.name)

    return {
      logger,
      dataTestid,
    }
  },

  props: {
    name: {
      type: String,
      required: true,
    },

    isRequired: {
      type: Boolean,
      default: false,
    },

    radioValue: [String, Number, Boolean],

    value: [String, Number, Boolean],

    partiallyChecked: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    size: {
      type: String || Number,
      default: '',
    },

    error: String,

    mode: {
      type: String,
      default: 'checkbox',
      validator: (value) => {
        return [
          'checkbox',
          'radio',
        ].indexOf(value) !== -1
      },
    },

    theme: {
      type: String,
      default: 'default',
    },

    reorder: {
      type: Boolean,
      default: false,
    },

    tooltip: {
      type: String,
      required: false,
    },

    tooltipPosition: {
      type: String,
      default: 'right-center',
      validator: (value) => TOOLTIP_POSITIONS.includes(value),
    },

    invalid: {
      type: Boolean,
      required: false,
    },

    noMargin: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      hover: false,
    }
  },

  computed: {

    checked () {
      if (this.mode === 'radio') {
        return this.value === this.radioValue
      } else {
        return !!this.value
      }
    },

    inputId () {
      return Math.ceil(Math.random() * 100) + `${this.mode}_${this.name}`
    },
  },

  methods: {

    onInput (e) {
      this.logger.debug('input', e)
      if (this.mode === 'radio') {
        if (e.target.checked) {
          this.$emit('input', {
            name: this.name,
            value: this.radioValue,
          })
        }
      } else {
        this.$emit('input', {
          name: this.name,
          value: e.target.checked,
        })
      }
    },
  },

}
</script>

<style lang="scss" module>
@layer legacy-views {

.container {
  display: flex;
  user-select: none;
  cursor: pointer;
  align-items: center;

  &Disabled {
    cursor: default;
    .replacement {
      background-color: var(--color-gray-15);
    }
  }
  &toggler,
  &toggler--revert,
  &toggler--revert-order {
    margin-left: 1rem;
    align-items: center;
  }

  &togglerNoMargin,
  &toggler--revertNoMargin,
  &toggler--revert-orderNoMargin {
    margin-left: 0;
    align-items: center;
  }

  &toggler--revert-order {
    .label {
      margin-left: 0;
      margin-right: 0.75rem;
    }
  }
}

.replacement {
  position: relative;
  background: var(--color-white);
  border: 1px solid var(--color-gray-14);
  height: 1.5rem;
  width: 1.5rem;
  flex: 0 0 1.5rem;
  border-radius: 6px;
  transition: $transition;
  overflow: hidden;
  .tick {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-weight: bold;
    opacity: 0;
    transition: $transition;
    color: var(--color-white);
    width: 20px;
    height: 20px;
  }

  &Radio {
    border-radius: 50%!important;
    &:before {
      content: " ";
      opacity: 0;
      height: 12px;
      width: 12px;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: $color-primary;
      transition: $transition;
      border-radius: 50%;
    }
  }

  &PartiallyChecked,
  &Checked {
    border-color: $color-primary;
    background: $color-primary;
    padding: 0;
    &.replacementRadio {
      background: initial;
      border-width: 2px;
    }
    .tick {
      opacity: 1;
    }
    &:before {
      opacity: 1;
    }
  }
  &Invalid {
    border: 2px solid var(--b-color-red);
  }
}

.sm {
  .replacement {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    .tick {
      width: 14px;
      height: 14px;
    }
    &Radio {

      &:before {
        height: 8px;
        width: 8px;
      }
    }
  }
}

.toggler {
  position: relative;
  display: block;
  height: 32px;
  width: 51px;
  background-color: var(--color-gray-15);
  transition: $transition;
  border: 1px solid rgba(0, 0, 0, .1);
  border-radius: 16px;
  box-shadow: inset 0 2px 4px 0 rgba(125, 125, 125, .1);
  overflow: hidden;
  &:before {
    border-radius: 50%;
    position: absolute;
    content: "";
    height: 29px;
    width: 29px;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    background-color: var(--color-white);
    border: 0.5px solid transparent(var(--color-black), .1);
    box-shadow: 0 4px 7px 0 transparent(var(--color-black), .1);
    transition: $transition;
  }
  &Revert {
    border-color: rgba(0, 0, 0, .1);
    background-color: $color-status-green;
    transform: rotate(180deg);

    &.togglerChecked {
      background-color: $color-status-red;
      border: 1px solid rgba(0, 0, 0, .1);
    }
    &Order {
      background-color: $color-status-red;
      border: 1px solid rgba(0, 0, 0, .1);
      flex-direction: row-reverse;
      &Grayed {
        background-color: var(--color-gray-15);
      }
    }
  }
  &Checked {
    border-color: rgba(0, 0, 0, .1);
    background-color: $color-status-green;
    &:before {
      left: 49px - 29px;
    }
  }
}

.realCheck {
  position: absolute;
  visibility: hidden;
  &:disabled {
    + .replacement {
      &Checked {
        background-color: var(--color-gray-12);
        border-color: var(--color-gray-12);
        .tick {
          color: var(--color-gray-16);
        }
      }
    }
  }
}

.label {
  flex: 1;
  @include size('14');
  margin-left: 12px;
}

.inputReordered {
  order: 1;
}
.contentReordered {
  order: 0;
  margin-left: 0;
}

.valueDescription {
  @include size('10');
  color: var(--color-gray-08);
  text-align: center;
  max-width: 60px;
}

}</style>
